<template>
  <draggable
    tag='div'
    class='theme-gallery'
    :class="{ 'theme-gallery--has-any-selected': !!selected, 'theme-gallery--row-direction': rowDirection}"
    v-bind="{ group: 'groups', draggable: isDraggable, delay: 400, delayOnTouchOnly: true, touchStartThreshold: 3, fallbackTolerance: 3, filter: selectorIsFiltered }"
    v-model='dataCollection'
    @update="$emit('reorder-group', dataCollection.map(i => i.id), galleryMemberId); $emit('reorder', reorder())"
  >
    <div class='theme-gallery__group js-group-item' v-for='group in dataCollection' :key='group.id'>
      <slot name='header' :group='group'>
        <h3 v-if='group[groupName]' class='theme-gallery__heading'>
          {{ group[groupName] }}
        </h3>
      </slot>
      <div class='theme-gallery__tools mb-3' v-if='toolbar'>
        <a class='theme-btn theme-btn--secondary theme-btn--square theme-btn--square--small' @click='toggleAddForm(group.id)' v-if='addForm' :id='group.id'>
          <i class='icon-plus'></i>
        </a>
        <a class='theme-btn theme-btn--secondary theme-btn--square theme-btn--square--small' @click='toggleToolbar(group.id)' v-if='editButton'>
          <i class='icon-settings' style='font-size: 20px;'></i>
        </a>
        <a class='theme-btn theme-btn--secondary theme-btn--square theme-btn--square--small' @click="$emit('delete-group', group.id, galleryMemberId)" v-if='removeButton'>
          <i class='icon-delete' style='font-size: 20px;'></i>
        </a>
        <slot name='extra' :group='group'></slot>
      </div>
      <div>
        <div class='loader-bar' v-if='singleGallery && showLoaderFor === group.id'></div>
        <div class='loader-bar loader-bar--loop' v-else style='display: none;'></div>
        <div v-if='group.toolbar'>
          <slot name='toolbar' :item='group'></slot>
        </div>
        <draggable
          tag='ul'
          :draggable='selector'
          class='theme-gallery__list'
          v-model='group[items]'
          v-bind="{ group: 'items', delay: 400, delayOnTouchOnly: true, touchStartThreshold: 3, fallbackTolerance: 3 }"
          @update="$emit('reorder-item', group[items].map(i => i.id), group.id, galleryMemberId); $emit('reorder', reorder())"
          @add="$emit('reorder-outer-item', group[items].map(i => i.id), group.id, galleryMemberId)"
          @change="$event.removed || $event.added ? $emit('change', $event, group.id, galleryMemberId) : null"
        >
          <template v-for='(item, index) in group[items]'>
            <li
              class='theme-gallery__item theme-gallery__item--draggable'
              :class="{ 'theme-gallery__item--is-selected': selected === item }"
              :key='item.id'
            >
              <div class='theme-gallery__item-inner'>
                <slot :gallery-id='galleryMemberId' :item='item' :group='group' :groupheader='group[groupName]' :is-selected='selected === item'></slot>
              </div>
              <div class='theme-gallery__item-index'>{{ itemIndex(index + 1) }}</div>
              <v-lightcase-holder v-if='!disableLightcaseHolder' :image-src='item.url' :name='`lightcase-${item.id}`' :download-link='item.fileUrl' :current-document='item' :disable-rotate='disableRotate' :details='group'></v-lightcase-holder>
            </li>
          </template>
          <li
            :for='`file${group.id}`'
            class='theme-gallery__item theme-gallery__item-to-drop'
            v-if='!selected'
            @dragover='resetEvent($event)'
            @dragleave='resetEvent($event)'
            @drop='handleUpload($event, group.id, galleryMemberId)'
            @click='triggerFileInputClick(`fileInput${group.id}`)'
          >
            <i class='icon-plus' style='font-size:20px;margin-bottom: 10px;'/>
            {{locales && locales.common && locales.common.upload}}
          </li>
        </draggable>
        <input
          type='file'
          v-if='acceptImages'
          multiple
          @change='handleUpload($event, group.id, galleryMemberId)'
          accept='image/*'
          v-show='false'
          :ref='`fileInput${group.id}`'
        />
        <input
          type='file'
          v-else
          multiple
          @change='handleUpload($event, group.id, galleryMemberId)'
          v-show='false'
          :ref='`fileInput${group.id}`'
        />
        <div
          class='mt-3'
          v-else-if='group[items].includes(selected)'
        >
          <slot name='toolbar' :selected='selected' :facility='group'></slot>
        </div>
      </div>
      <div v-if='group.addForm' style='display: flex;'>
        <slot name='add-form' :item='group'></slot>
      </div>
    </div>
  </draggable>
</template>

<script>
  import _ from 'lodash';
  import draggable from 'vuedraggable';
  import 'lightcase/src/css/lightcase.css';
  import configureLightcase from '../lightcase.js';
  import VLightcaseHolder from '../admin/components/VLightcaseHolder';
  import sweetalert from 'sweetalert';
  import { mapGetters, mapActions } from "vuex";

  export default {
    components: {
      draggable,
      VLightcaseHolder,
    },
    data() {
      return {
        selected: null,
        dataCollection: Object.assign([], this.collection),
        isDraggable: this.disableDragAndDrop ? '.none-of-current-items-draggable' : '.theme-gallery__group',
        selector: window.app.methods.isTouchDevice() ? '.theme-gallery__item--draggable' : '.theme-gallery__item',
        selectorIsFiltered: window.app.methods.isTouchDevice() ? '.theme-gallery__item-to-drop' : '',
      };
    },
    computed: {
      ...mapGetters(["locales"]),
    },
    methods: {
      reorder() {
        return this.dataCollection.map(group => {
          let groupItems = {};
          groupItems[group.id] = group[this.items].map(i => i.id);
          return groupItems;
        });
      },
      toggleToolbar(groupId) {
        let currentGroupIndex = this.dataCollection.findIndex(item => {
          return item.id === groupId;
        });
        let currentGroup = this.dataCollection[currentGroupIndex];
        currentGroup.toolbar = !currentGroup.toolbar;
        this.dataCollection[currentGroupIndex] = {
          ...currentGroup,
        };
        this.$emit('toolbar-toggled', {
          index: currentGroupIndex,
          state: !!currentGroup.toolbar,
        });
        this.dataCollection = [...this.dataCollection];
      },
      toggleAddForm(groupId) {
        let currentGroupIndex = this.dataCollection.findIndex(item => {
          return item.id === groupId;
        });
        let currentGroup = this.dataCollection[currentGroupIndex];
        currentGroup.addForm = !currentGroup.addForm;
        this.dataCollection[currentGroupIndex] = {
          ...currentGroup,
        };
        this.dataCollection = [...this.dataCollection];
      },
      resetEvent(event) {
        if (!window.app.methods.isTouchDevice()) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      triggerFileInputClick(ref) {
        this.$refs[ref][0].click();
      },
      handleUpload(event, groupId, galleryId) {
        event.preventDefault();
        event.stopPropagation();

        let files,
          filesNumber = 0;

        filesNumber = event.target.closest('.js-group-item').querySelectorAll('.theme-gallery__item--draggable').length;

        if (
          event.dataTransfer &&
          event.dataTransfer.files &&
          event.dataTransfer.files.length
        ) {
          files = event.dataTransfer.files;

          if (!this.allowSingleFileOnly) {
            this.$emit('upload', files, groupId, galleryId);
          } else {
            if (filesNumber === 0 && event.dataTransfer.files.length === 1) {
              this.$emit('upload', files, groupId, galleryId);
            } else {
              sweetalert('Information', 'You may upload only one file', 'info');
            }
          }
        } else if (
          event.target &&
          event.target.files &&
          event.target.files.length
        ) {
          files = event.target.files;

          if (!this.allowSingleFileOnly) {
            this.$emit('upload', files, groupId, galleryId);
          } else {
            if (filesNumber === 0 && event.target.files.length === 1) {
              this.$emit('upload', files, groupId, galleryId);
            } else {
              sweetalert('Information', 'You may upload only one file', 'info');
            }
          }
        }
      },
      itemIndex(num) {
        return num < 10 ? '0' + num : num.toString();
      },
    },
    props: {
      collection: {
        default: () => [],
        type: Array,
      },
      galleryMemberId: {
        default: 0,
        type: Number,
      },
      items: {
        default: 'items',
        type: String,
      },
      groupName: {
        default: 'name',
        type: String,
      },
      toolbar: {
        default: false,
        type: Boolean,
      },
      addForm: {
        default: false,
        type: Boolean,
      },
      editButton: {
        default: true,
        type: Boolean,
      },
      removeButton: {
        default: true,
        type: Boolean,
      },
      disableRotate: {
        default: false,
        type: Boolean,
      },
      showLoaderFor: {
        default: 0,
        type: Number,
      },
      disableDragAndDrop: {
        default: false,
        type: Boolean,
      },
      acceptImages: {
        default: false,
        type: Boolean,
      },
      singleGallery: {
        default: true,
        type: Boolean,
      },
      allowSingleFileOnly: {
        default: false,
        type: Boolean,
      },
      rowDirection: {
        default: false,
        type: Boolean,
      },
      disableLightcaseHolder: {
        default: false,
        type: Boolean,
      },
    },
    created() {
      if (!this.singleGallery) {
        $(document).on('click', '.theme-gallery__item-to-drop', function() {
          $('.loader-bar--loop').removeClass('is-visible');
          $(this).parents('.js-group-item').find('.loader-bar--loop').addClass('is-visible');
        });
      }
    },
    watch: {
      collection(value) {
        this.dataCollection = value;
      },
    },
    beforeMount() {
      configureLightcase();
    },
    beforeUpdate() {
      configureLightcase();
    },
    updated() {
      this.$root.$emit('galleryUpdated');
    },
  };
</script>
